import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { resolveGanpatiOverlayRule } from "@/commons/ganpatiOverlayConfig";

/**
 * FE-only, config-driven celebratory greeting shown once per page-load on
 * the pooja detail page — gated entirely by GANPATI_OVERLAY_RULES
 * (commons/ganpatiOverlayConfig.js). Renders nothing unless the current
 * pooja slug + location + language match a configured rule, so it is a
 * no-op for every other pooja page.
 */
export default function GanpatiGreetingOverlay({ poojaSlug, location, language }) {
  const rule = useMemo(
    () => resolveGanpatiOverlayRule(poojaSlug, location, language),
    [poojaSlug, location, language],
  );

  const [visible, setVisible] = useState(false);
  const [shownForKey, setShownForKey] = useState(null);

  useEffect(() => {
    if (!rule) return;
    const key = `${poojaSlug}|${location}|${language}`;
    if (shownForKey === key) return; // already shown for this combo this session
    setShownForKey(key);
    setVisible(true);
    const timer = setTimeout(() => setVisible(false), rule.durationMs || 3800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule, poojaSlug, location, language]);

  if (!rule) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          onClick={() => setVisible(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-950/70 via-black/60 to-red-950/70 backdrop-blur-sm" />

          {/* Card */}
          <motion.div
            className="relative w-full max-w-sm rounded-3xl overflow-hidden bg-gradient-to-b from-amber-50 via-orange-50 to-amber-100 shadow-2xl border border-amber-300/60"
            initial={{ scale: 0.7, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.85, opacity: 0, y: 10 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Glow */}
            <div className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 w-56 h-56 rounded-full bg-amber-300/50 blur-3xl" />

            {/* Sparkles */}
            <span className="absolute top-4 left-6 w-1.5 h-1.5 rounded-full bg-amber-400 animate-ping" style={{ animationDuration: "2s" }} />
            <span className="absolute top-10 right-8 w-1 h-1 rounded-full bg-orange-400 animate-ping" style={{ animationDuration: "2.6s", animationDelay: "0.3s" }} />
            <span className="absolute bottom-24 left-8 w-1.5 h-1.5 rounded-full bg-yellow-400 animate-ping" style={{ animationDuration: "2.2s", animationDelay: "0.6s" }} />

            <button
              aria-label="Close"
              onClick={() => setVisible(false)}
              className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/70 hover:bg-white text-amber-800 text-sm flex items-center justify-center transition-colors z-10"
            >
              ✕
            </button>

            <div className="relative px-6 pt-9 pb-7 flex flex-col items-center text-center">
              <motion.img
                src="/icons/ganpati.svg"
                alt="Ganpati Bappa"
                className="w-24 h-24 drop-shadow-lg"
                initial={{ scale: 0, rotate: -8 }}
                animate={{ scale: [0, 1.15, 1], rotate: [-8, 4, 0] }}
                transition={{ duration: 0.7, ease: "easeOut" }}
              />

              <motion.h2
                className="mt-4 text-2xl font-extrabold bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.4 }}
              >
                {rule.greeting}
              </motion.h2>

              {rule.subGreeting && (
                <motion.p
                  className="mt-1 text-sm font-semibold text-amber-700"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.4 }}
                >
                  {rule.subGreeting}
                </motion.p>
              )}

              {(rule.sthapanaDate || rule.visarjanDate) && (
                <motion.div
                  className="mt-5 w-full grid grid-cols-2 gap-3"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55, duration: 0.4 }}
                >
                  {rule.sthapanaDate && (
                    <div className="rounded-2xl bg-white/70 border border-amber-200 px-3 py-2.5">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-amber-500">{rule.sthapanaLabel || "Sthapana"}</p>
                      <p className="text-xs font-bold text-amber-900 mt-0.5">{rule.sthapanaDate}</p>
                    </div>
                  )}
                  {rule.visarjanDate && (
                    <div className="rounded-2xl bg-white/70 border border-amber-200 px-3 py-2.5">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-amber-500">{rule.visarjanLabel || "Visarjan"}</p>
                      <p className="text-xs font-bold text-amber-900 mt-0.5">{rule.visarjanDate}</p>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
